import React, { useEffect, useRef, useState } from 'react';
import { TrendingUp } from 'lucide-react';
import * as am5 from '@amcharts/amcharts5';
import * as am5xy from '@amcharts/amcharts5/xy';
import am5themes_Animated from '@amcharts/amcharts5/themes/Animated';

const TrendLine = () => {
  const chartRef = useRef(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!chartRef.current) return;

    // Create root element
    let root = am5.Root.new(chartRef.current);

    // Set themes
    root.setThemes([am5themes_Animated.new(root)]);

    // Create chart
    let chart = root.container.children.push(
      am5xy.XYChart.new(root, {
        panX: false,
        panY: false,
        wheelX: 'none',
        wheelY: 'none',
        layout: root.verticalLayout,
        paddingLeft: 0,
        paddingRight: 0
      })
    );

    // Generate sample data with positive and negative feedback
    const generateData = () => {
      const data = [];
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      
      for (let i = 0; i < 12; i++) {
        const positiveBase = 60 + Math.random() * 30;
        const negativeBase = -(20 + Math.random() * 25);
        
        data.push({
          month: months[i],
          positive: Math.floor(positiveBase),
          negative: Math.floor(negativeBase),
          trend: Math.floor(positiveBase + negativeBase)
        });
      }
      return data;
    };

    const data = generateData();

    // Create axes
    let xAxis = chart.xAxes.push(
      am5xy.CategoryAxis.new(root, {
        categoryField: 'month',
        renderer: am5xy.AxisRendererX.new(root, {
          minGridDistance: 30
        }),
        tooltip: am5.Tooltip.new(root, {})
      })
    );

    xAxis.data.setAll(data);

    // Style X axis
    xAxis.get('renderer').labels.template.setAll({
      fontSize: 11,
      fill: am5.color(0x00e5ff),
      fontWeight: '500'
    });

    xAxis.get('renderer').grid.template.setAll({
      stroke: am5.color(0x1f1f1f),
      strokeOpacity: 0.3
    });

    let yAxis = chart.yAxes.push(
      am5xy.ValueAxis.new(root, {
        renderer: am5xy.AxisRendererY.new(root, {})
      })
    );

    // Style Y axis
    yAxis.get('renderer').labels.template.setAll({
      fontSize: 11,
      fill: am5.color(0xb24bf3),
      fontWeight: '500'
    });

    yAxis.get('renderer').grid.template.setAll({
      stroke: am5.color(0x1f1f1f),
      strokeOpacity: 0.3
    });

    // Create positive series (stacked column)
    let positiveSeries = chart.series.push(
      am5xy.ColumnSeries.new(root, {
        name: 'Positive Feedback',
        xAxis: xAxis,
        yAxis: yAxis,
        valueYField: 'positive',
        categoryXField: 'month',
        stacked: true,
        fill: am5.color(0x39ff14),
        stroke: am5.color(0x39ff14)
      })
    );

    positiveSeries.columns.template.setAll({
      tooltipText: 'Positive: {valueY}',
      width: am5.percent(60),
      strokeWidth: 2,
      strokeOpacity: 0.8,
      fillOpacity: 0.7
    });

    // Add glow effect to positive columns
    positiveSeries.columns.template.adapters.add('fillGradient', function() {
      return am5.LinearGradient.new(root, {
        stops: [{
          color: am5.color(0x39ff14),
          opacity: 0.9
        }, {
          color: am5.color(0x39ff14),
          opacity: 0.6
        }],
        rotation: 90
      });
    });

    positiveSeries.data.setAll(data);

    // Create negative series (stacked column)
    let negativeSeries = chart.series.push(
      am5xy.ColumnSeries.new(root, {
        name: 'Negative Feedback',
        xAxis: xAxis,
        yAxis: yAxis,
        valueYField: 'negative',
        categoryXField: 'month',
        stacked: true,
        fill: am5.color(0xff6b35),
        stroke: am5.color(0xff6b35)
      })
    );

    negativeSeries.columns.template.setAll({
      tooltipText: 'Negative: {valueY}',
      width: am5.percent(60),
      strokeWidth: 2,
      strokeOpacity: 0.8,
      fillOpacity: 0.7
    });

    // Add glow effect to negative columns
    negativeSeries.columns.template.adapters.add('fillGradient', function() {
      return am5.LinearGradient.new(root, {
        stops: [{
          color: am5.color(0xff6b35),
          opacity: 0.6
        }, {
          color: am5.color(0xff6b35),
          opacity: 0.9
        }],
        rotation: 90
      });
    });

    negativeSeries.data.setAll(data);

    // Create trend line series
    let trendSeries = chart.series.push(
      am5xy.LineSeries.new(root, {
        name: 'Net Trend',
        xAxis: xAxis,
        yAxis: yAxis,
        valueYField: 'trend',
        categoryXField: 'month',
        stroke: am5.color(0x00e5ff),
        fill: am5.color(0x00e5ff)
      })
    );

    trendSeries.strokes.template.setAll({
      strokeWidth: 3
    });

    // Add bullets to trend line
    trendSeries.bullets.push(function() {
      let circle = am5.Circle.new(root, {
        radius: 5,
        fill: am5.color(0x00e5ff),
        stroke: am5.color(0x0d0d0d),
        strokeWidth: 2
      });
      
      return am5.Bullet.new(root, {
        sprite: circle
      });
    });

    trendSeries.data.setAll(data);

    // Customize tooltips
    let tooltip = am5.Tooltip.new(root, {
      getFillFromSprite: false,
      autoTextColor: false
    });

    tooltip.get('background').setAll({
      fill: am5.color(0x1a1a1a),
      fillOpacity: 0.95,
      stroke: am5.color(0x00e5ff),
      strokeWidth: 1
    });

    tooltip.label.setAll({
      fill: am5.color(0x00e5ff),
      fontSize: 12
    });

    positiveSeries.columns.template.set('tooltip', tooltip);
    negativeSeries.columns.template.set('tooltip', tooltip);

    // Add cursor
    chart.set('cursor', am5xy.XYCursor.new(root, {
      behavior: 'none',
      xAxis: xAxis
    }));

    // Set background
    chart.set('background', am5.Rectangle.new(root, {
      fill: am5.color(0x141414),
      fillOpacity: 0
    }));

    // Animate on load
    positiveSeries.appear(1000);
    negativeSeries.appear(1000);
    trendSeries.appear(1000);
    chart.appear(1000, 100);

    setIsLoading(false);

    return () => {
      root.dispose();
    };
  }, []);

  return (
    <div className="relative bg-dark-card border border-dark-border rounded-lg p-4 sm:p-6 overflow-hidden group h-full flex flex-col"
         style={{
           background: '#141414',
           boxShadow: '0 0 2px rgba(0, 229, 255, 0.3)'
         }}>

      {/* Background Icon */}
      <div className="absolute -right-8 -top-8 opacity-[0.04] text-cyan-accent pointer-events-none"
           style={{ filter: 'drop-shadow(0 0 25px currentColor) drop-shadow(0 0 50px currentColor)' }}>
        <TrendingUp size={150} strokeWidth={1} />
      </div>

      {/* Header */}
      <div className="relative z-10 mb-4">
        <div className="flex items-center space-x-3 mb-2">
          <TrendingUp className="text-cyan-accent" size={24} strokeWidth={2.5}
                   style={{ filter: 'drop-shadow(0 0 10px currentColor) drop-shadow(0 0 20px currentColor)' }} />
          <h3 className="text-lg sm:text-xl font-bold text-cyan-accent"
              style={{ textShadow: '0 0 20px currentColor, 0 0 40px currentColor' }}>
            Feedback Trend
          </h3>
        </div>
        <p className="text-xs text-gray-500">Monthly positive vs negative feedback analysis</p>
        
        {/* Legend */}
        <div className="flex flex-wrap gap-3 mt-3 text-xs">
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 rounded"
                 style={{
                   background: '#39ff14',
                   boxShadow: '0 0 8px rgba(57, 255, 20, 0.5)'
                 }} />
            <span className="text-gray-400">Positive</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 rounded"
                 style={{
                   background: '#ff6b35',
                   boxShadow: '0 0 8px rgba(255, 107, 53, 0.5)'
                 }} />
            <span className="text-gray-400">Negative</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-0.5 h-3 rounded"
                 style={{
                   background: '#00e5ff',
                   boxShadow: '0 0 8px rgba(0, 229, 255, 0.5)',
                   width: '12px'
                 }} />
            <span className="text-gray-400">Net Trend</span>
          </div>
        </div>
      </div>

      {/* amCharts Trend Line */}
      <div className="relative flex-1">
        {isLoading && (
          <div className="absolute inset-0 flex items-center justify-center z-20">
            <div className="text-cyan-accent animate-pulse">Loading chart...</div>
          </div>
        )}
        <div
          ref={chartRef}
          className="relative z-10 w-full h-full"
          style={{
            minHeight: '350px'
          }}
        />
      </div>

      {/* Neon glow border effect */}
      <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none text-cyan-accent rounded-lg"
           style={{ boxShadow: 'inset 0 0 20px currentColor, 0 0 20px currentColor' }} />
    </div>
  );
};

export default TrendLine;




